// Hälsa för ett hus, ett fritidshus eller en annan plats (b3): temperaturen inne och ute, läget,
// zonerna (Nobø eller rummen i Home Assistant), energin, givarna och källorna. Och planerna för
// "Åka till" och "Resa bort", som också båtarna visar.
// b11: är platsen ej i drift och navet tyst längre än gränsen för tyst nav, säger ett kort högst
// upp att värdena är de senast kända, med tiden, och att de inte bedöms.
// b13: kortet Vädret vid huset, när platsen har en väderstation (vader.tsx). b20-1: kortet Ute
// visar temperaturen ur uteTemp: väderstationen när den är färsk, annars husets givare, med källan
// i liten text.
// b14: kortet Jordfukt, när huset har jordgivare (jordfukt.tsx).
// b20-2: kortet Ytterdörr, när huset har ett lås via Home Assistant på Pi:n (ytterdorr.tsx).

import {
  datumOchTid,
  type Halsa,
  inneKalla,
  inneTemperatur,
  type Planvisning,
  type Signalvarde,
  sedan,
  tal,
  uteTemp,
} from "@batkoll/gemensam";
import { Kort, Rad } from "../komponenter.tsx";
import { arGammalt, jaNej, tal1 } from "../visa.ts";
import { Badtemperaturkort, Givarhalsa } from "./givare.tsx";
import { Jordfuktkort } from "./jordfukt.tsx";
import { Vaderkort } from "./vader.tsx";
import { Ytterdorrkort } from "./ytterdorr.tsx";

const LAGE: Record<string, string> = {
  normal: "Normal (veckoprogrammet)",
  komfort: "Komfort",
  eko: "Eko",
  borta: "Borta",
  av: "Av",
  hemma: "Hemma",
};

const LARM: Record<string, string> = {
  av: "Av",
  hemma: "Hemma",
  borta: "Borta",
  natt: "Natt",
  semester: "Semester",
  utlost: "Utlöst",
  okant: "Okänt",
};

/** Signalens instanser, till exempel zonerna: [["Stue", värde], …]. */
function instanser(h: Halsa, signal: string): [string, Signalvarde][] {
  return Object.entries(h.signaler)
    .filter(([k]) => k.startsWith(`${signal}@`))
    .map(([k, v]): [string, Signalvarde] => [k.slice(signal.length + 1), v])
    .sort(([a], [b]) => a.localeCompare(b, "sv"));
}

export function Planer(props: {
  planer: Planvisning[];
  kanAndra: boolean;
  taBort: (id: string) => void;
}) {
  if (props.planer.length === 0) return null;
  return (
    <Kort rubrik="Planer" bred>
      {props.planer.map((p) => (
        <div key={p.id} class="plan">
          <ul>
            {p.rader.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          {props.kanAndra && (
            <button type="button" class="knapp-lank" onClick={() => props.taBort(p.id)}>
              Ta bort planen
            </button>
          )}
        </div>
      ))}
    </Kort>
  );
}

export function Husvy(props: { h: Halsa; nu: number }) {
  const { h, nu } = props;
  const s = (nyckel: string) => h.signaler[nyckel];
  const v = (nyckel: string) => s(nyckel)?.varde;
  const g = (nyckel: string) => arGammalt(s(nyckel), nu);
  // b20-1: temperaturen ute: väderstationen före husets givare, som på kortet (uteTemp).
  const ute = uteTemp(h.signaler, nu);
  // b24-1: samma regel som översikten: husets givare, annars medelvärdet av zonerna.
  const inne = inneTemperatur(h.signaler);
  const zoner = new Set(
    ["hus.zon.temp", "hus.zon.komfort", "hus.zon.lage"].flatMap((sig) =>
      instanser(h, sig).map(([z]) => z),
    ),
  );
  const lackor = instanser(h, "hus.vattenlacka");
  const dorrar = instanser(h, "hus.dorr");
  // b17: förmågorna från Home Assistant och Homey som ägaren har kopplat i guiden.
  const fonster = instanser(h, "hus.fonster");
  const las = instanser(h, "hus.las");
  const rok = instanser(h, "hus.rok");
  const effekter = instanser(h, "hus.effekt");
  const energier = instanser(h, "hus.energi");
  const rokNu = rok.some(([, x]) => x.varde === true);
  const fukt = [
    ...(s("hus.fukt") ? [["Inne", s("hus.fukt")] as const] : []),
    ...instanser(h, "hus.fukt"),
  ];
  const harNobo = s("nobo.kontakt") !== undefined;
  const harHa = s("ha.kontakt") !== undefined;
  const harHomey = s("homey.kontakt") !== undefined;
  const lage = h.tolkning.husLage;
  const lackaNu = lackor.some(([, x]) => x.varde === true) || v("hus.vattenlacka") === true;

  return (
    <div class="rutnat">
      {h.senastKanda != null && (
        <Kort rubrik="Senast kända värden" bred>
          <p class="meddelande">
            Platsen är ej i drift, och navet hördes senast av {sedan(h.senastKanda, nu)} (
            {datumOchTid(h.senastKanda)}). Värdena nedan är de senast kända, och bedöms inte.
          </p>
        </Kort>
      )}
      {lackaNu && (
        <Kort rubrik="Vattenläcka" ton="akut" bred>
          <p class="meddelande">
            Vatten:{" "}
            {lackor
              .filter(([, x]) => x.varde === true)
              .map(([z]) => z)
              .join(", ") || "huset"}
          </p>
        </Kort>
      )}

      {rokNu && (
        <Kort rubrik="Brandvarnaren" ton="akut" bred>
          <p class="meddelande">
            Brandvarnaren larmar:{" "}
            {rok
              .filter(([, x]) => x.varde === true)
              .map(([z]) => z)
              .join(", ")}
          </p>
        </Kort>
      )}

      {h.ytterdorr && <Ytterdorrkort y={h.ytterdorr} tidszon={h.plats.tidszon} />}

      <Kort rubrik="Inne">
        <div class="stort">{inne === null ? "–" : `${tal(inne.varde, 1)} °C`}</div>
        {inne && inneKalla(inne) && <small class="ute-kalla">{inneKalla(inne)}</small>}
        {fukt.map(([namn, x]) => (
          <Rad key={namn} namn={`Fukt, ${namn.toLowerCase()}`} gammalt={arGammalt(x, nu)}>
            {tal1(x?.varde, "%", 0)}
          </Rad>
        ))}
      </Kort>

      <Kort rubrik="Ute">
        <div class="stort">
          {ute === null ? "–" : `${tal(ute.varde, 1)} °C`}
          {ute?.gammalt && <span class="gammalt-text"> (gammalt, {sedan(ute.tid, nu)})</span>}
        </div>
        {ute?.text && <small class="ute-kalla">{ute.text}</small>}
      </Kort>

      {h.vaderstation && (
        <Vaderkort v={h.vaderstation} bat={false} tidszon={h.plats.tidszon} nu={nu} />
      )}

      <Kort rubrik="Läge">
        <div class="stort mellan">
          {lage === "hemma" ? "Hemma" : lage === "borta" ? "Borta" : "–"}
        </div>
        {v("nobo.lage") !== undefined && (
          <Rad namn="Nobø">{LAGE[String(v("nobo.lage"))] ?? "–"}</Rad>
        )}
        {v("hus.larm") !== undefined && (
          <Rad namn="Larmet">{LARM[String(v("hus.larm"))] ?? "–"}</Rad>
        )}
        {/* Båda spärrarna (b3-1): molnets, och platsens i navets egna inställningar. */}
        <Rad namn="Styrning">
          {h.styrningslage?.text ?? (h.plats.styrning ? "molnet på" : "molnet av")}
        </Rad>
        {/* Elen och värmemodellen, som förvärmningen räknar med (b4). */}
        {h.el && <Rad namn="El">{h.el.text}</Rad>}
        {h.el && <Rad namn="Värmemodell">{h.el.modell}</Rad>}
      </Kort>

      {zoner.size > 0 && (
        <Kort rubrik="Zoner" bred>
          {[...zoner]
            .sort((a, b) => a.localeCompare(b, "sv"))
            .map((z) => {
              const t = s(`hus.zon.temp@${z}`);
              const komfort = v(`hus.zon.komfort@${z}`);
              const eko = v(`hus.zon.eko@${z}`);
              const zl = v(`hus.zon.lage@${z}`);
              const program = v(`hus.zon.program@${z}`);
              // b24: med golvvärme eller termostat på elementet gäller inte hubbens värde: vi visar var
              // det ställs, inte siffran.
              const styrning = v(`hus.zon.hubbstyrning@${z}`);
              const komfortPaElement = styrning === "inget" || styrning === "eko";
              const ekoPaElement = styrning === "inget";
              const bada = typeof komfort === "number" && typeof eko === "number";
              const varden = (): string | null => {
                // Som förut när hubben styr båda: "komfort 21, eko 16 °C".
                if (!komfortPaElement && !ekoPaElement) {
                  return bada ? `komfort ${tal(komfort, 0)}, eko ${tal(eko, 0)} °C` : null;
                }
                const k = komfortPaElement
                  ? "komfort på elementet"
                  : typeof komfort === "number"
                    ? `komfort ${tal(komfort, 0)} °C`
                    : null;
                const e = ekoPaElement
                  ? "eko på termostaten"
                  : typeof eko === "number"
                    ? `eko ${tal(eko, 0)} °C`
                    : null;
                return k && e ? `${k}, ${e}` : null;
              };
              const detaljer = [
                zl ? (LAGE[String(zl)] ?? String(zl)) : null,
                varden(),
                typeof program === "string" ? program : null,
              ].filter(Boolean);
              return (
                <Rad key={z} namn={z} gammalt={arGammalt(t, nu)}>
                  {t && typeof t.varde === "number" ? `${tal(t.varde, 1)} °C` : "ingen givare"}
                  {detaljer.length > 0 ? ` · ${detaljer.join(" · ")}` : ""}
                </Rad>
              );
            })}
          {harNobo && (
            <p class="liten">
              Många termostater rapporterar inte temperaturen. Då står det "ingen givare".
            </p>
          )}
        </Kort>
      )}

      {(v("hus.effekt") !== undefined ||
        v("hus.energi") !== undefined ||
        effekter.length > 0 ||
        energier.length > 0) && (
        <Kort rubrik="Energi">
          {(v("hus.effekt") !== undefined || v("hus.energi") !== undefined) && (
            <>
              <Rad namn="Effekt" gammalt={g("hus.effekt")}>
                {tal1(v("hus.effekt"), "W", 0)}
              </Rad>
              <Rad namn="Energi" gammalt={g("hus.energi")}>
                {tal1(v("hus.energi"), "kWh", 1)}
              </Rad>
            </>
          )}
          {effekter.map(([z, x]) => (
            <Rad key={`e${z}`} namn={`Effekt, ${z}`} gammalt={arGammalt(x, nu)}>
              {tal1(x.varde, "W", 0)}
            </Rad>
          ))}
          {energier.map(([z, x]) => (
            <Rad key={`k${z}`} namn={`Energi, ${z}`} gammalt={arGammalt(x, nu)}>
              {tal1(x.varde, "kWh", 1)}
            </Rad>
          ))}
        </Kort>
      )}

      {(lackor.length > 0 ||
        dorrar.length > 0 ||
        fonster.length > 0 ||
        las.length > 0 ||
        rok.length > 0) && (
        <Kort rubrik="Givare">
          {lackor.map(([z, x]) => (
            <Rad key={`l${z}`} namn={`Vattenläcka, ${z.toLowerCase()}`} gammalt={arGammalt(x, nu)}>
              {jaNej(x.varde, "Vatten!", "Torrt")}
            </Rad>
          ))}
          {dorrar.map(([z, x]) => (
            <Rad key={`d${z}`} namn={z} gammalt={arGammalt(x, nu)}>
              {jaNej(x.varde, "Öppen", "Stängd")}
            </Rad>
          ))}
          {fonster.map(([z, x]) => (
            <Rad key={`f${z}`} namn={z} gammalt={arGammalt(x, nu)}>
              {jaNej(x.varde, "Öppet", "Stängt")}
            </Rad>
          ))}
          {las.map(([z, x]) => (
            <Rad key={`s${z}`} namn={z} gammalt={arGammalt(x, nu)}>
              {jaNej(x.varde, "Låst", "Olåst")}
            </Rad>
          ))}
          {rok.map(([z, x]) => (
            <Rad key={`r${z}`} namn={z} gammalt={arGammalt(x, nu)}>
              {jaNej(x.varde, "Larmar!", "Ingen rök")}
            </Rad>
          ))}
        </Kort>
      )}

      {(s("kyl.temp") || s("frys.temp")) && (
        <Kort rubrik="Kyl och frys">
          {s("kyl.temp") && (
            <Rad namn="Kylskåpet" gammalt={g("kyl.temp")}>
              {tal1(v("kyl.temp"), "°C")}
            </Rad>
          )}
          {s("frys.temp") && (
            <Rad namn="Frysen" gammalt={g("frys.temp")}>
              {tal1(v("frys.temp"), "°C")}
            </Rad>
          )}
        </Kort>
      )}

      {h.badtemperatur && (
        <Badtemperaturkort b={h.badtemperatur} tidszon={h.plats.tidszon} nu={nu} />
      )}

      {h.jordfukt && h.jordfukt.length > 0 && <Jordfuktkort g={h.jordfukt} nu={nu} />}

      <Givarhalsa h={h} nu={nu} />

      {(harNobo || harHa || harHomey) && (
        <Kort rubrik="Källorna">
          {harHa && (
            <Rad namn="Home Assistant">{jaNej(v("ha.kontakt"), "Kontakt", "Ingen kontakt")}</Rad>
          )}
          {harHomey && (
            <Rad namn="Homey">{jaNej(v("homey.kontakt"), "Kontakt", "Ingen kontakt")}</Rad>
          )}
          {harNobo && (
            <Rad namn="Nobø-hubben">{jaNej(v("nobo.kontakt"), "Kontakt", "Ingen kontakt")}</Rad>
          )}
          {typeof v("nobo.komponenter") === "string" && v("nobo.komponenter") && (
            <p class="liten">{String(v("nobo.komponenter"))}</p>
          )}
        </Kort>
      )}
    </div>
  );
}
